'use client'

import { useEffect } from 'react'

/**
 * Removes Google's cross-domain / click tracking parameters from the visible
 * URL after they have been consumed.
 *
 * GTM / GA4 (and the Conversion Linker) read the `_gl` linker blob — which
 * stitches the `_ga` / `_gcl_au` identity across a cross-domain hop — on page
 * load, before this effect runs on hydration. Stripping the param here
 * therefore keeps cross-domain measurement working while getting rid of the
 * ugly `?_gl=1*...` in the address bar.
 *
 * Campaign params (`utm_*`) and the hash are intentionally preserved.
 *
 * Consumers mount this once (e.g. in a root layout or `_app`); it renders
 * nothing.
 */
const TRACKING_PARAMS = [
  '_gl',
  '_ga',
  '_gac',
  '_gcl_au',
  '_gcl_aw',
  '_gcl_dc',
  '_gcl_gb',
  '_gcl_gf',
  '_gcl_ha',
  'gclid',
  'gclsrc',
  'dclid',
  'wbraid',
  'gbraid',
  'fbclid',
  'msclkid',
]

export default function StripTrackingParams() {
  useEffect(() => {
    if (typeof window === 'undefined' || !window.history?.replaceState) {
      return
    }

    const url = new URL(window.location.href)
    let changed = false

    for (const param of TRACKING_PARAMS) {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param)
        changed = true
      }
    }

    if (changed) {
      const search = url.searchParams.toString()
      const cleaned = url.pathname + (search ? `?${search}` : '') + url.hash
      window.history.replaceState(window.history.state, '', cleaned)
    }
  }, [])

  return null
}
